import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, MessageCircle, ArrowRight, Zap, Wrench, BadgeCheck, Recycle, Clock3,
} from "lucide-react";
import { SplitLines, FadeUp, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import ContactForm from "../components/ContactForm";
import { SERVICES } from "../data/services";
import { LOCATIONS } from "../data/locations";
import { IMG, PHONE_TEL, PHONE_DISPLAY, waLink, DEFAULT_WA_MSG } from "../data/site";

const STATS = [
  { v: "30–60", u: "min response", i: Zap },
  { v: "24/7", u: "dispatch, 365 days", i: Clock3 },
  { v: "12", u: "UK regions covered", i: BadgeCheck },
  { v: "100%", u: "mobile — we come to you", i: Wrench },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end grain overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img
            src={IMG.hero}
            alt="Car on a dark road at night with Motion Tyres mobile fitting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/55 to-[#0B0B0C]/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C]/80 via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-40">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="pulse-dot font-telemetry text-[11px] sm:text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6"
            data-testid="hero-live-badge"
          >
            24/7 dispatch — vans live in your area
          </motion.p>

          <h1 className="font-display font-extrabold uppercase leading-[0.92] text-[15vw] sm:text-7xl lg:text-8xl tracking-tight max-w-5xl">
            <SplitLines
              delay={0.35}
              lines={["FLAT TYRE?", "WE COME TO YOU."]}
            />
            <SplitLines
              delay={0.62}
              accentLast
              lines={["24/7 — ACROSS TWELVE UK REGIONS."]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-6 max-w-xl text-zinc-300 text-base sm:text-lg leading-relaxed"
          >
            Mobile tyre fitting, puncture repair and wheel alignment — at your
            home, office or roadside. From the Midlands to the South Coast:
            Coventry, London, Reading, Bristol, Cardiff, Cambridge,
            Northampton, Oxford, Southampton, Bournemouth, Bath and
            Peterborough.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a
              href={PHONE_TEL}
              data-testid="hero-call-button"
              className="btn-glow flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#FF5500] hover:bg-[#FF6A1A] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em]"
            >
              <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
            </a>
            <a
              href={waLink(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-emerald-500/70 bg-[#0B0B0C]/60 backdrop-blur-md text-emerald-400 hover:bg-emerald-500 hover:text-black transition-colors font-telemetry text-sm uppercase tracking-[0.12em]"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp us
            </a>
          </motion.div>

          {/* telemetry stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A] max-w-4xl"
            data-testid="hero-stats"
          >
            {STATS.map(({ v, u, i: Icon }) => (
              <div key={u} className="bg-[#0B0B0C]/80 backdrop-blur-sm px-5 py-4">
                <Icon className="w-4 h-4 text-[#FF5500] mb-2" />
                <p className="font-display font-extrabold text-2xl sm:text-3xl text-white leading-none">
                  {v}
                </p>
                <p className="font-telemetry text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {u}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* ============ SERVICES ============ */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="services-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <FadeUp>
            <Eyebrow>What we do</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3">
              The workshop that
              <br />
              <span className="text-outline">drives to you.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} className="max-w-md text-zinc-400">
            Every service runs from a fully equipped van — fitting, balancing,
            repair and alignment kit on board, and a qualified technician at
            the wheel.
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.slug} delay={i * 0.06}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`home-service-card-${s.slug}`}
                className="group block card-line bg-[#131316] border border-[#27272A] rounded-sm overflow-hidden h-full"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover img-zoom"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#131316] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 font-telemetry text-[10px] uppercase tracking-[0.25em] bg-[#0B0B0C]/80 backdrop-blur-sm border border-[#27272A] text-[#FF5500] px-3 py-1.5 rounded-full">
                    {String(i + 1).padStart(2, "0")} — Service
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-extrabold uppercase text-2xl text-white">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    {s.excerpt.slice(0, 110)}…
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-[#FF5500]">
                    Explore service
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="border-y border-[#27272A] bg-[#0E0E10]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <FadeUp>
            <Eyebrow>The process</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl mt-3">
              Back on the road in four moves.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A] mt-12">
            {[
              { n: "01", t: "Call or WhatsApp", d: "Send your registration and location. Fixed quote before the van moves — day or night." },
              { n: "02", t: "We dispatch", d: "The nearest van heads your way with the right tyres already loaded. 30–60 min typical." },
              { n: "03", t: "Fitted on the spot", d: "Mounted, balanced, torqued to spec and pressure-checked. Old tyres taken away." },
            ].map((step, i) => (
              <FadeUp key={step.n} delay={i * 0.1} className="bg-[#0E0E10] p-8">
                <p className="font-display font-extrabold text-6xl text-outline">{step.n}</p>
                <h3 className="font-display font-extrabold uppercase text-2xl mt-4 text-white">{step.t}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{step.d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ============ COVERAGE ============ */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="coverage-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <FadeUp>
            <Eyebrow>Coverage areas</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3">
              Twelve regions.
              <br />
              <span className="text-[#FF5500]">One number.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} className="max-w-md text-zinc-400">
            Tap your area to see local response times, the roads and districts
            we cover, and to book a van straight to your spot.
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {LOCATIONS.map((l, i) => (
            <FadeUp key={l.slug} delay={i * 0.05}>
              <Link
                to={`/areas/${l.slug}`}
                data-testid={`home-location-card-${l.slug}`}
                className="group relative block h-64 rounded-sm overflow-hidden border border-[#27272A] card-line"
              >
                <img
                  src={l.image}
                  alt={`Motion Tyres coverage in ${l.name}`}
                  className="absolute inset-0 w-full h-full object-cover img-zoom"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between gap-3">
                  <div>
                    <h3 className="font-display font-extrabold uppercase text-3xl text-white leading-none">
                      {l.name}
                    </h3>
                    <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-2">
                      {l.region}
                    </p>
                  </div>
                  <span className="shrink-0 font-telemetry text-[10px] uppercase tracking-[0.15em] text-black bg-[#FF5500] px-3 py-1.5 rounded-full">
                    {l.eta}
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ============ WHY US ============ */}
      <section className="border-y border-[#27272A] bg-[#131316]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <FadeUp>
            <Eyebrow>Why Motion Tyres</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3">
              A garage in a van,
              <br />
              <span className="text-outline">at your door.</span>
            </h2>
            <div className="mt-8 space-y-5 max-w-lg">
              {[
                { i: Zap, t: "True 24/7 response", d: "Midnight motorway or 6am driveway — dispatch runs around the clock, every day of the year." },
                { i: Wrench, t: "Fully equipped vans", d: "Balancing, repair and alignment kit on board, stocked with the sizes your car actually wears." },
                { i: BadgeCheck, t: "Workshop-grade work", d: "Manufacturer torque specs, dynamic balancing and BS AU 159 compliant repairs as standard." },
                { i: Recycle, t: "Everything taken care of", d: "Old tyres recycled, pressures set, TPMS reset — you just turn the key." },
              ].map(({ i: Icon, t, d }) => (
                <div key={t} className="flex gap-4">
                  <div className="shrink-0 w-11 h-11 flex items-center justify-center border border-[#FF5500]/40 rounded-sm">
                    <Icon className="w-5 h-5 text-[#FF5500]" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold uppercase text-xl text-white">{t}</h3>
                    <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="relative">
            <div className="frame-cut overflow-hidden border border-[#27272A]">
              <img
                src={IMG.fitting2}
                alt="Motion Tyres technician fitting a tyre"
                className="w-full h-[420px] sm:h-[520px] object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-[#FF5500] text-black px-6 py-4 hidden sm:block">
              <p className="font-display font-extrabold uppercase text-2xl leading-none">30–60 min</p>
              <p className="font-telemetry text-[10px] uppercase tracking-[0.2em] mt-1">typical response</p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ============ CONTACT / BOOK ============ */}
      <section id="book" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="home-contact-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <FadeUp>
            <Eyebrow>Book a van</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3">
              Tell us where.
              <br />
              <span className="text-[#FF5500]">We'll be there.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
              Send the form and it lands straight in our WhatsApp with your
              live location attached — or skip the queue and call the dispatch
              line directly. Use the live-location button and we'll pinpoint
              your exact spot before you finish typing.
            </p>
            <div className="mt-8 space-y-3 font-telemetry text-xs uppercase tracking-[0.2em] text-zinc-300">
              <p className="flex items-center gap-3">
                <Clock3 className="w-4 h-4 text-[#FF5500]" /> Open 24 hours — every day
              </p>
              <p className="flex items-center gap-3">
                <BadgeCheck className="w-4 h-4 text-[#FF5500]" /> 12 UK regions — Midlands to the South Coast
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.12}>
            <ContactForm />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
